<script lang="ts">
import { defineComponent } from 'vue'
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue'
import UserDataService from '@/services/UserDataService'
import type User from '@/types/User'
import type ResponseData from '@/types/ResponseData'
import type { LaravelPaginatedResponse } from '@/types/ApiResponses'
import * as _ from 'lodash'
import moment from 'moment'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import { Table, TableHeader, TableBody, TableRow, TableCell } from '@/components/ui/table'
import { TableCard, TableSearch, TablePagination, TableEmptyState } from '@/components/common/table'

export default defineComponent({
  name: 'user-list',
  components: {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableCell,
    TableCard,
    TableSearch,
    TablePagination,
    TableEmptyState,
    PageBreadcrumb,
  },
  data() {
    return {
      users: [] as User[],
      currentUser: {} as User,
      searchTerm: '',
      loaded: false,
      selectedColumn: 'id',
      selectedDirection: 'desc' as 'asc' | 'desc',
      moment: moment(),
      pagination: {} as LaravelPaginatedResponse<User>['data'],
      pagesRange: 5
    }
  },
  methods: {
    retrieveUsers(page: number, type: string) {
      if (type === 'prev') {
        page--;
      } else if (type === 'next') {
        page++;
      }
      this.loaded = false;
      const request = this.searchTerm.trim()
        ? UserDataService.searchUsers(this.searchTerm.trim(), page)
        : UserDataService.getAllUsers(page);
      request
        .then((response: ResponseData) => {
          this.users = response.data.data.data;
          this.pagination = response.data.data;
          this.loaded = true;
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message);
          this.loaded = true;
        });
    },
    refreshList() {
      this.retrieveUsers(1, '');
      this.currentUser = {} as User;
    },
    setOrderResultsBy(column: string) {
      if (this.selectedColumn == column) {
        this.selectedDirection = this.selectedDirection == 'desc' ? 'asc' : 'desc';
      } else {
        this.selectedDirection = 'asc';
      }
      this.selectedColumn = column;
    },
  },
  computed: {
    pages() {
      const currentPage = this.pagination.current_page;
      const lastPage = this.pagination.last_page;
      const startPage = Math.max(1, currentPage - Math.floor(this.pagesRange / 2));
      const endPage = Math.min(lastPage, startPage + this.pagesRange - 1);

      return _.range(startPage, endPage + 1);
    },
    orderedItems() {
      return _.orderBy(this.users, [this.selectedColumn], [this.selectedDirection]);
    },
  },
  watch: {
    searchTerm(_newTerm: string) {
      this.retrieveUsers(1, ''); // Call retrieveUsers when searchTerm changes
    }
  },
  mounted() {
    this.retrieveUsers(1, '');
  },
})
</script>

<template>
  <main class="flex-1">
    <div class="py-6">
      <div class="max-w-(--breakpoint-2xl) mx-auto">
        <PageBreadcrumb page-title="Zoznam klientov" />
      </div>
      <TableCard title="Klienti">
        <template #controls>
          <div class="flex w-full items-center gap-3 sm:w-auto">
            <div class="flex-1 sm:w-64 sm:flex-initial md:w-72">
              <TableSearch v-model="searchTerm" />
            </div>
          </div>
        </template>
          <div class="max-w-full overflow-x-auto min-w-0">
            <Table class="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
              <TableHeader
                class="border-y border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900"
              >
                      <TableRow>
                        <TableCell is-header
                          class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                          <span class="inline-flex items-center" :class="{ 'text-brand-500': selectedColumn == 'id' }">
                            ID
                            <button>
                              <svg @click="setOrderResultsBy('id')" xmlns="http://www.w3.org/2000/svg"
                                class="ml-1 size-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                                <path
                                  d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                              </svg>
                            </button>
                          </span>
                        </TableCell>
                        <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                          <span class="inline-flex items-center" :class="{ 'text-brand-500': selectedColumn == 'name' }">
                            Meno
                            <button>
                              <svg @click="setOrderResultsBy('name')" xmlns="http://www.w3.org/2000/svg"
                                class="ml-1 size-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                                <path
                                  d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                              </svg></button></span>
                        </TableCell>
                        <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                          <span class="inline-flex items-center"
                            :class="{ 'text-brand-500': selectedColumn == 'email' }">
                            Email
                            <button>
                              <svg @click="setOrderResultsBy('email')" xmlns="http://www.w3.org/2000/svg"
                                class="ml-1 size-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                                <path
                                  d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                              </svg></button></span>
                        </TableCell>
                        <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                          <span class="inline-flex items-center" :class="{ 'text-brand-500': selectedColumn == 'date' }">
                            Dátum zmeny
                            <button>
                              <svg @click="setOrderResultsBy('date')" xmlns="http://www.w3.org/2000/svg"
                                class="ml-1 size-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                                <path
                                  d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                              </svg>
                            </button>
                          </span>
                        </TableCell>
                        <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Edit</TableCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody class="divide-y divide-gray-100 dark:divide-gray-800">
                      <TableRow class="hover:bg-gray-200 dark:hover:bg-gray-700" :class="{ 'bg-red-50': user.deleted_at }"
                        v-for="user in orderedItems" :key="user.id">
                        <TableCell class="px-6 py-4 text-sm font-medium text-gray-800 dark:text-white/90">{{ user.id }}</TableCell>
                        <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-white/90">
                          {{ user.first_name }} {{ user.last_name }}
                          <span v-if="user.deleted_at"
                            class="ml-2 inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-red-100 text-red-800">
                            Zmazaný
                          </span>
                        </TableCell>
                        <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-white/90">{{ user.email }}</TableCell>
                        <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-white/90">{{ user.updated_at }}</TableCell>
                        <TableCell class="px-6 py-4 text-sm font-medium text-right">
                          <router-link :to="'/user-detail/' + user.id"
                            class="text-green-500 hover:text-green-700">Edit</router-link>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                    <TableEmptyState
                      v-if="loaded && !users.length"
                      title="Žiadni klienti"
                      hint="Skúste upraviť hľadanie."
                      colspan="5"
                    />
                  </Table>
                </div>
                <template #footer>
                  <TablePagination
                    v-if="pagination.last_page > 1"
                    :current-page="pagination.current_page"
                    :total-pages="pagination.last_page"
                    @page-change="retrieveUsers($event, '')"
                  />
                </template>
              </TableCard>
            </div>
          </main>
        </template>
